import 'react-advanced-cropper/dist/style.css'

import { useCallback, useEffect, useRef, useState } from 'react'
import { CropperRef } from 'react-advanced-cropper'
import { getZoomFactor } from 'advanced-cropper/extensions/absolute-zoom'
import { observer } from 'mobx-react-lite'

import { useObservable } from '@/components/useObservable'

import { EditContext, EditConfig } from './types'
import { ImageCropper } from './ImageCropper'
import { Toolbar } from './Toolbar'
import { setModeDefaults } from './modes'

export type ImageEditorProps = {
    image: File | Blob | string
    config: EditConfig
    onCancel: () => void
    onSubmit: (image: Blob) => void
}

//
// This will return a Blog of the modified image or undefined for any cancelation or no changes
//

export const ImageEditor = observer(({ image, config, onCancel, onSubmit }: ImageEditorProps) => {
    const context = useObservable(() => {
        const result: EditContext = {
            config,
            mode: 'crop',
            zoom: 0,
            saturation: 0,
            contrast: 0,
            brightness: 0,
            hue: 0,
        }

        setModeDefaults(result)

        return result
    })

    const cropperRef = useRef<CropperRef>(null)

    const [imageSrc, setImageSrc] = useState<string>('')
    const [readyRefreshKey, setReadyRefreshKey] = useState(0)

    useEffect(() => {
        if (typeof image === 'string') {
            setImageSrc(image)
            return
        }

        const imageSrc = URL.createObjectURL(image)
        setImageSrc(imageSrc)

        return () => {
            URL.revokeObjectURL(imageSrc)
        }
    }, [image])

    useEffect(() => {
        if (cropperRef.current) {
            const zoomFactor = getZoomFactor(
                cropperRef.current.getState(),
                cropperRef.current.getSettings(),
                context.zoom
            )

            cropperRef.current.zoomImage(zoomFactor, { transitions: false })
        }
    }, [context.zoom])

    const handleReady = useCallback(() => {
        setReadyRefreshKey((key) => key + 1)
    }, [])

    // The dialog animates its scale on open; refresh after it settles so the cropper uses the final boundary size.
    useEffect(() => {
        if (!readyRefreshKey) {
            return
        }

        let delayedRefresh: number | undefined
        const animationFrame = window.requestAnimationFrame(() => {
            cropperRef.current?.refresh()

            delayedRefresh = window.setTimeout(() => {
                cropperRef.current?.refresh()
            }, 150)
        })

        return () => {
            window.cancelAnimationFrame(animationFrame)

            if (delayedRefresh !== undefined) {
                window.clearTimeout(delayedRefresh)
            }
        }
    }, [readyRefreshKey])

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-neutral-950 p-4 dark:bg-neutral-800">
            <ImageCropper cropperRef={cropperRef} src={imageSrc} context={context} onReady={handleReady} />
            <Toolbar cropperRef={cropperRef} context={context} onCancel={onCancel} onSubmit={onSubmit} />
        </div>
    )
})
